import {isEmpty, isNil} from 'ramda';

import {unwrapFilterValue} from './unwrapFilterValue';

/**
 * Converts EagGrid's internal filter state shape to the flat format the backend API expects.
 *
 * ## Problem
 *
 * Every EagGrid filter component stores its value wrapped in `{value: X}` — this
 * is the internal representation used by `FilterState` (e.g. `{name: {value: 'John'}}`).
 * The backend `createDataQuery` endpoint however expects flat values:
 * `string | string[] | {from, to}`.  Sending the wrapped form causes schema
 * validation to fail with a 400 error.
 *
 * ## What this function does
 *
 * 1. **Unwraps** `{value: X}` → `X` for every key in the input object.
 * 2. **Strips** sentinel values that indicate "no filter active" — the key is
 *    omitted from the result entirely:
 *    - `false` — used by toggle/switch filters when off
 *    - `null` / `undefined` — cleared filters
 *    - empty arrays / strings / objects — cleared multi-choice, text, and range
 *      filters. The backend rejects explicit `null` filter values with a 400
 *      (T20-135733); the old datagrid never sent them either — AG Grid's
 *      `setFilterModel` removes null-valued entries from the model.
 * 3. Values that are **not** wrapped in `{value: X}` are passed through as-is,
 *    so the function is safe to call on already-unwrapped objects.
 *
 * This replaces the `processFilterValues` step that existed in the original
 * datagrid implementation but was missing from the EagGrid migration.
 *
 * @param filters - Raw filter state from `filtersModel.filterValues` (may be
 *   wrapped or unwrapped).
 * @returns Flat filter object ready to be sent to `http.createDataQuery`.
 */
export const unwrapFilterValues = (filters: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(filters)) {
    const value = unwrapFilterValue(rawValue);

    if (isNil(value) || value === false || isEmpty(value)) {
      continue;
    }

    result[key] = value;
  }

  return result;
};
